import * as React from 'react';
import { useHistory } from 'react-router-dom';

import classNames from 'classnames';
import { inject } from 'mobx-react';
import { observer } from 'mobx-react-lite';

import { CurrentUserStore } from '@stores';

import { IBillingInfo } from '@api';

import { getConfig, URLS, PLATFORM_PAYMENT_ID } from '@base/constants';
import { EStore } from '@base/enums';
import { getTranslation } from '@base/utils';

import { OuterWrapper } from '@ui';

import { PaymentError, PaymentGoBackButton, PaymentSuccess, PaymentWait } from './components';
import { usePaymentResultReducer } from './usePaymentResultReducer';

import * as s from './PaymentResult.scss';

export enum EPaymentState {
	SUCCESS,
	ERROR,
	PENDING
}

export interface IPaymentData extends Partial<IBillingInfo> {
	status: EPaymentState;
}

export const PaymentResult = inject(
	EStore.CURRENT_USER,
)(observer((
	{ currentUserStore }: { currentUserStore: CurrentUserStore },
) => {
	const history = useHistory();
	const paymentTranslation = getTranslation('pages.PaymentResult');
	const {
		paymentData: {
			status,
			price,
			appId,
			serviceName,
			isHybrid,
		} = {},
	} = usePaymentResultReducer(currentUserStore.currentUser);
	const onBack = () => {
		if (!isHybrid || getConfig().isAndroidApp) {
			if (appId === PLATFORM_PAYMENT_ID) {
				// we shouldn't use react router navigation here
				// to avoid getting to the authorization page
				window.location.href = '/';
			} else {
				history.push(`${URLS.APPS}/${appId}`);
			}
		} else {
			window.close();
		}
	};

	return (
		<OuterWrapper className={classNames(
			s.block,
			{ [s.blockFullSize]: getConfig().isAndroidApp },
		)}>
			<div>
				{ status === EPaymentState.SUCCESS &&
					<PaymentSuccess
						price={Number(price)}
						serviceName={serviceName}
						appId={appId}
					/>
				}
				{ status === EPaymentState.ERROR && <PaymentError/> }
				{ status === EPaymentState.PENDING && <PaymentWait/> }
			</div>
			{(status === EPaymentState.SUCCESS || status === EPaymentState.ERROR) &&
				<PaymentGoBackButton onClick={onBack}>
					{appId === PLATFORM_PAYMENT_ID
						? paymentTranslation('backToMain')
						: paymentTranslation('backToGame')}
				</PaymentGoBackButton>
			}
		</OuterWrapper>
	);
}));
